import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, AlertCircle, ArrowRight } from "lucide-react";
import { adminAuth, formatApiErrorDetail } from "../lib/adminApi";

export default function AdminLogin() {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!password.trim()) {
      setError("Enter your admin password.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await adminAuth.login(password);
      adminAuth.setToken(data.token);
      navigate("/admin/leads", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6 py-12 relative overflow-hidden">
      <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 h-1 bg-[#FF2222]" />

      <div className="w-full max-w-md relative">
        <div className="mb-10 text-center">
          <a href="/" className="font-display text-3xl uppercase tracking-tight inline-flex items-baseline gap-2 hover:text-[#FF2222] transition-colors" data-testid="admin-home-link">
            iza3nee <span className="text-[#FF2222]">creations</span>
          </a>
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mt-3">Lead Management · Admin</div>
        </div>

        <form
          data-testid="admin-login-form"
          onSubmit={submit}
          className="border border-white/10 bg-[#0a0a0a] p-8 space-y-6"
        >
          <div className="flex items-center gap-3 pb-4 border-b border-white/10">
            <div className="bg-[#FF2222] p-2">
              <Lock size={16} className="text-white" />
            </div>
            <div>
              <div className="font-display uppercase text-xl text-white leading-none tracking-tight">Admin Access</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mt-1">Restricted area</div>
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.3em] text-white/50 block mb-2">Password</label>
            <input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="iza-input w-full"
              autoFocus
            />
          </div>

          {error && (
            <div data-testid="admin-login-error" className="flex items-center gap-2 text-[#FF6666] text-sm border-l-2 border-[#FF2222] pl-3">
              <AlertCircle size={14} /> {error}
            </div>
          )}

          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={loading}
            className="w-full bg-[#FF2222] hover:bg-white hover:text-black text-white uppercase font-bold tracking-wider text-xs px-6 py-4 inline-flex items-center justify-center gap-3 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? "Authenticating..." : (<>Enter Dashboard <ArrowRight size={14} /></>)}
          </button>
        </form>

        <div className="text-center text-[10px] uppercase tracking-[0.25em] text-white/30 mt-6">
          Sessions expire after 24 hours
        </div>
      </div>
    </div>
  );
}
